import React, {useState, useEffect, useRef} from 'react';
import {find} from 'lodash';
import {VariableSizeList as List} from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';
import {Switch} from 'components/form-items';
import cx from 'classnames';

const ButtonGroup = (props: any) => {
  const {employees, formProps, edit, setCurrentEmployee, currentEmployee} =
    props;
  const {reset} = formProps;
  const [isOpenAll, setIsOpenAll] = useState(false);
  const [isCloseAll, setIsCloseAll] = useState(false);

  const handleCloseAll = () => {
    const formValues = formProps.getValues();
    const keys = Object.keys(formValues);
    const _d = employees.map((d: any) => {
      keys.forEach(t => {
        d[t]?.enabled && (d[t].enabled = false);
      });
      return d;
    });
    edit({
      employeePayrollItems: _d,
    });
    const currentData = find(_d, o => {
      return o?.employee.id === currentEmployee.employee.id;
    });
    setCurrentEmployee(currentData);
    reset(currentData);
  };
  const handleOpenAll = () => {
    const formValues = formProps.getValues();
    const keys = Object.keys(formValues);
    const _d = employees.map((d: any) => {
      keys.forEach(t => {
        d[t].hasOwnProperty('enabled') && (d[t].enabled = true);
      });
      return d;
    });
    edit({
      employeePayrollItems: _d,
    });
    const currentData = find(_d, o => {
      return o?.employee.id === currentEmployee.employee.id;
    });
    setCurrentEmployee(currentData);
    reset(currentData);
  };

  useEffect(() => {
    const formValues = employees[0] || {};
    const keys = Object.keys(formValues).filter(_key => {
      return formValues[_key].hasOwnProperty('enabled');
    });
    const isOpen = employees.every((d: any) => {
      return keys.every(t => d[t].enabled);
    });
    const isClose = employees.every((d: any) => {
      return keys.every(t => d[t].enabled === false);
    });
    setIsOpenAll(isOpen);
    setIsCloseAll(isClose);
  }, [employees]);

  return (
    <div className="bg-[#e9eced] w-[178px] h-[55px] flex items-center justify-center text-[14px]">
      <span
        className={cx(
          'cursor-pointer',
          isOpenAll
            ? 'text-black text-opacity-30 cursor-not-allowed'
            : 'hover:text-brand',
        )}
        onClick={handleOpenAll}
      >
        全部開啟
      </span>
      <span className="w-0 h-[25px] border border-[#c6c7c8] border-opacity-[.39] mx-4" />
      <span
        className={cx(
          'cursor-pointer',
          isCloseAll
            ? 'text-black text-opacity-30 cursor-not-allowed'
            : 'hover:text-brand',
        )}
        onClick={handleCloseAll}
      >
        全部關閉
      </span>
    </div>
  );
};

const Row = (props: any) => {
  const {
    formProps,
    employees,
    currentEmployee,
    setCurrentEmployee,
    edit,
    allErrors,
    updateAllErrors,
    disabled,
    index,
    style,
    setRowHeight,
  } = props;
  const {getValues, formState, reset} = formProps;
  const data = employees[index];
  const isError = formState.isSubmitted && allErrors[index];
  const isCurrent = data.employee.id === currentEmployee?.employee?.id;
  const handleChange = (nextChecked: boolean, e: any) => {
    e.stopPropagation();
    const formValues = formProps.getValues();
    const keys = Object.keys(formValues);
    const _setEmployee = (d: any) => {
      keys.forEach(t => {
        d[t].hasOwnProperty('enabled') && (d[t].enabled = nextChecked);
      });
      return d;
    };
    const _d2 = employees.map((d: any) => {
      if (d.employee.id === data.employee.id) {
        return _setEmployee(d);
      }
      return d;
    });
    edit({
      employeePayrollItems: _d2,
    });
    if (isCurrent) {
      const currentData = find(_d2, o => {
        return o?.employee.id === currentEmployee?.employee?.id;
      });
      setCurrentEmployee(currentData);
      reset(currentData, {
        errors: true, // errors will not be reset
        dirtyFields: true, // dirtyFields will not be reset
        isDirty: true, // dirty will not be reset
        isSubmitted: true, // isSubmitted will not be reset
      });
    }
  };

  const isEnable = Object.keys(data).some((t: any) => {
    return data[t]?.enabled;
  });

  const rowRef = useRef<any>({});

  useEffect(() => {
    if (rowRef.current) {
      setRowHeight(index, rowRef.current.clientHeight);
    }
    // eslint-disable-next-line
  }, [rowRef]);
  return (
    <div
      data-test-id={`employee-${index}`}
      style={style}
      key={`employeeList_${index}`}
      className={cx(
        'min-h-10 bg-transparent hover:bg-[#ccc]',
        isCurrent ? 'current-edit' : '',
        isError ? 'error text-[#ff3b30]' : '',
        disabled ? 'cursor-not-allowed' : 'cursor-pointer',
        isCurrent
          ? 'border border-[#26abe3]'
          : index === 0
          ? 'border border-y-white border-x-transparent'
          : 'border-b border-x border-b-white border-x-transparent',
      )}
    >
      <div
        ref={rowRef}
        className={cx('flex items-center justify-start w-full p-2.5')}
        onClick={() => {
          if (!disabled) {
            if (formState.isSubmitted) {
              updateAllErrors();
            }
            edit({
              employeePayrollItems: employees.map((d: any) => {
                if (d.employee.id === currentEmployee.employee.id) {
                  return getValues();
                }
                return d;
              }),
            });
            setCurrentEmployee(data);
          }
        }}
      >
        <div
          data-test-id={`enable-employee-data-${index}}`}
          className="flex items-center mr-2.5"
        >
          <Switch
            onChange={handleChange}
            checked={isEnable}
            className="react-switch"
          />
        </div>
        <div className="break-all flex items-center h-full">{`${data.employee.jobNo}/${data.employee.name}`}</div>
      </div>
    </div>
  );
};

export default function EmployeeSidebar(props: any) {
  const {employees} = props;
  const listRef = useRef<any>({});
  const rowHeights = useRef<any>({});

  function getRowHeight(index: number) {
    return rowHeights.current[index] || 40;
  }

  function setRowHeight(index: number, size: number) {
    listRef.current.resetAfterIndex(0);
    rowHeights.current = {...rowHeights.current, [index]: size};
  }

  return (
    <AutoSizer>
      {({height}) => {
        return (
          <>
            <ButtonGroup {...props} />
            <List
              itemCount={employees.length}
              itemSize={getRowHeight}
              height={height}
              width={178}
              itemData={employees}
              className={'bg-[#e9eced]'}
              ref={listRef}
            >
              {({index, style}) => {
                return (
                  <Row
                    index={index}
                    style={style}
                    setRowHeight={setRowHeight}
                    {...props}
                  />
                );
              }}
            </List>
          </>
        );
      }}
    </AutoSizer>
  );
}
